import i18n from "i18next";
import { initReactI18next } from "react-i18next";

import common from "@/i18n/locales/en/common.json";
import dashboard from "@/i18n/locales/en/dashboard.json";
import navigation from "@/i18n/locales/en/navigation.json";
import privacy from "@/i18n/locales/en/privacy.json";
import settings from "@/i18n/locales/en/settings.json";
import upload from "@/i18n/locales/en/upload.json";

/**
 * i18n scaffolding. English ships complete; the structure is ready for community
 * translations (see CONTRIBUTING.md). Language is detected from the browser and
 * persisted to localStorage. All resources are bundled — no network fetch.
 */

export const LANGUAGE_KEY = "catalogkit.language";
export const DEFAULT_LANGUAGE = "en";

/** Languages available in the switcher. Add new locales here as they land. */
export const SUPPORTED_LANGUAGES: { code: string; label: string }[] = [
  { code: "en", label: "English" },
];

export const resources = {
  en: { common, navigation, upload, dashboard, privacy, settings },
} as const;

export const NAMESPACES = ["common", "navigation", "upload", "dashboard", "privacy", "settings"];

function detectLanguage(): string {
  const stored = typeof localStorage !== "undefined" ? localStorage.getItem(LANGUAGE_KEY) : null;
  if (stored && SUPPORTED_LANGUAGES.some((l) => l.code === stored)) return stored;
  const browser =
    typeof navigator !== "undefined"
      ? (navigator.language.split("-")[0] ?? DEFAULT_LANGUAGE)
      : DEFAULT_LANGUAGE;
  return SUPPORTED_LANGUAGES.some((l) => l.code === browser) ? browser : DEFAULT_LANGUAGE;
}

void i18n.use(initReactI18next).init({
  resources,
  lng: detectLanguage(),
  fallbackLng: DEFAULT_LANGUAGE,
  ns: NAMESPACES,
  defaultNS: "common",
  interpolation: { escapeValue: false },
  returnNull: false,
});

/** Change the active language and persist the choice. */
export function setLanguage(code: string): void {
  if (typeof localStorage !== "undefined") localStorage.setItem(LANGUAGE_KEY, code);
  void i18n.changeLanguage(code);
}

export default i18n;
